import { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Routes, useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { X } from 'lucide-react';

import {
  AdvisorOnboardingWelcomeStep,
  AdvisorOnboardingHeader,
  AdvisorOnboardingFooter,
  AdvisorOnboardingInvitingClientsStep,
  AdvisorOnboardingClientIntakeFormStep,
  AdvisorOnboardingCompleteYourProfileStep,
  AdvisorOnboardingCompletionStep,
} from '@client/components/advisor-onboarding';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@client/components/ui/tooltip';

import { selectHasSkippedOnboardingStep } from '@client/model/selectors';
import { setHasSkippedOnboardingStep } from '@client/model/advisorOnboardingModel';
import { useAdvisorOnboarding, ONBOARDING_STEPS } from '@client/hooks/useAdvisorOnboarding';

type AdvisorOnboardingStep =
  | 'welcome'
  | 'invite-client'
  | 'client-intake-form'
  | 'complete-profile'
  | 'completion';

interface AdvisorOnboardingStepConfig {
  id: AdvisorOnboardingStep;
  title: string;
  path: string;
  alertTitle?: string;
  alertDescription?: string;
}

const advisorOnboardingSteps: AdvisorOnboardingStepConfig[] = [
  {
    id: 'welcome',
    title: 'Welcome',
    path: 'welcome',
    alertTitle: 'Skip Introduction Video?',
    alertDescription:
      'This video provides important information about how to use Waterlily effectively with your clients. We recommend watching it to get the most out of the platform.',
  },
  {
    id: 'invite-client',
    title: 'Inviting Clients',
    path: 'invite-client',
    alertTitle: 'Skip Client Invitation Video?',
    alertDescription:
      'This section explains how to invite and manage clients on the platform. Understanding this process will help you onboard clients smoothly.',
  },
  {
    id: 'client-intake-form',
    title: 'Client Intake Form',
    path: 'client-intake-form',
    alertTitle: 'Skip Intake Form Overview?',
    alertDescription:
      'This section covers how the client intake form works and what information is collected. Knowing this will help you guide your clients through the process.',
  },
  {
    id: 'complete-profile',
    title: 'Complete Your Profile',
    path: 'complete-profile',
    alertTitle: 'Skip Profile Setup?',
    alertDescription:
      'A complete profile helps build trust with your clients and ensures they have the information they need. You can always update it later in settings.',
  },
  {
    id: 'completion',
    title: 'Get Started',
    path: 'completion',
  },
];

type VideoPlayedState = Record<AdvisorOnboardingStep, boolean>;

export function AdvisorOnboarding({ advisorId }: AdvisorIdProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const {
    checklist,
    refetch,
    markStepAsCompleted,
    markStepAsIncompleted,
    isStepCompleted,
    completedSteps,
  } = useAdvisorOnboarding(advisorId);

  const hasSkippedBefore = useSelector(selectHasSkippedOnboardingStep);

  const [videoPlayedState, setVideoPlayedState] = useState<VideoPlayedState>({
    'welcome': false,
    'invite-client': false,
    'client-intake-form': false,
    'complete-profile': false,
    'completion': false,
  });

  const handleVideoPlayed = useCallback((stepId: AdvisorOnboardingStep) => {
    setVideoPlayedState(prev => ({ ...prev, [stepId]: true }));
  }, []);

  const isProfileChecklistComplete =
    checklist?.hasConnectedEmailAccount &&
    checklist?.hasCalendarLink &&
    checklist?.hasBusinessAddress;

  useEffect(() => {
    refetch();
  }, [refetch]);

  const currentStep: AdvisorOnboardingStep = useMemo(() => {
    const path = location.pathname;
    const step = advisorOnboardingSteps.find(step => path.endsWith(step.path));

    return step?.id ?? 'welcome';
  }, [location.pathname]);

  useEffect(() => {
    if (currentStep === 'complete-profile' && !isProfileChecklistComplete) {
      markStepAsIncompleted('complete-profile');
    }
  }, [currentStep, isProfileChecklistComplete, markStepAsIncompleted]);

  const currentStepIndex: number = useMemo(() => {
    return advisorOnboardingSteps.findIndex(el => el.id === currentStep);
  }, [currentStep]);

  const navigateToStep = useCallback(
    (step: AdvisorOnboardingStep) => {
      const stepConfig = advisorOnboardingSteps.find(el => el.id === step);

      if (stepConfig) {
        navigate(`/advisors/${advisorId}/onboarding/${stepConfig.path}`);
      }
    },
    [advisorId, navigate],
  );

  const navigateToNextStep = useCallback(() => {
    if (currentStepIndex < advisorOnboardingSteps.length - 1) {
      markStepAsCompleted(currentStep);
      navigateToStep(advisorOnboardingSteps[currentStepIndex + 1].id);
    }
  }, [navigateToStep, currentStepIndex, currentStep, markStepAsCompleted]);

  const skipCurrentStep = useCallback(() => {
    if (currentStepIndex < advisorOnboardingSteps.length - 1) {
      dispatch(setHasSkippedOnboardingStep(true));
      navigateToStep(advisorOnboardingSteps[currentStepIndex + 1].id);
    }
  }, [navigateToStep, currentStepIndex, dispatch]);

  const navigateToPreviousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      navigateToStep(advisorOnboardingSteps[currentStepIndex - 1].id);
    }
  }, [navigateToStep, currentStepIndex]);

  const handleCompleteOnboarding = useCallback(() => {
    navigate(`/advisors/${advisorId}/clients`);
  }, [advisorId, navigate]);

  const currentStepConfig = advisorOnboardingSteps[currentStepIndex];
  const totalSteps = advisorOnboardingSteps.length;
  const isLastStep = currentStep === 'completion';

  const videoSteps: AdvisorOnboardingStep[] = ['welcome', 'invite-client', 'client-intake-form'];
  const isVideoStep = videoSteps.includes(currentStep);
  const isCurrentStepCompleted = isStepCompleted(currentStep);

  const areAllStepsCompleted = ONBOARDING_STEPS.every(step => completedSteps.includes(step));

  const isNextDisabled =
    (isVideoStep && !videoPlayedState[currentStep] && !isCurrentStepCompleted) ||
    (currentStep === 'complete-profile' && !isProfileChecklistComplete);

  const shouldHideSkip =
    isCurrentStepCompleted || (currentStep === 'complete-profile' && isProfileChecklistComplete);

  return (
    <div className="relative flex min-h-screen flex-col bg-white">
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={`/advisors/${advisorId}/clients`}
              className="absolute right-4 top-4 z-20 rounded-full p-2 text-gray-700 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Skip to dashboard"
            >
              <X className="size-6" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="left">Skip to dashboard</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <AdvisorOnboardingHeader
        totalSteps={totalSteps}
        currentStep={currentStepIndex}
        title={currentStepConfig?.title}
      />

      <div className="m-auto size-full max-w-5xl p-6">
        <Routes>
          <Route
            path="welcome"
            element={
              <AdvisorOnboardingWelcomeStep onVideoPlayed={() => handleVideoPlayed('welcome')} />
            }
          />
          <Route
            path="invite-client"
            element={
              <AdvisorOnboardingInvitingClientsStep
                onVideoPlayed={() => handleVideoPlayed('invite-client')}
              />
            }
          />
          <Route
            path="client-intake-form"
            element={
              <AdvisorOnboardingClientIntakeFormStep
                onVideoPlayed={() => handleVideoPlayed('client-intake-form')}
              />
            }
          />
          <Route
            path="complete-profile"
            element={<AdvisorOnboardingCompleteYourProfileStep refetch={refetch} />}
          />
          <Route
            path="completion"
            element={
              <AdvisorOnboardingCompletionStep
                advisorId={advisorId}
                completedSteps={completedSteps}
              />
            }
          />
        </Routes>
      </div>

      <AdvisorOnboardingFooter
        totalSteps={totalSteps}
        currentStep={currentStepIndex}
        onNext={isLastStep ? handleCompleteOnboarding : navigateToNextStep}
        onSkip={skipCurrentStep}
        onPrev={navigateToPreviousStep}
        title={currentStepConfig.alertTitle}
        description={currentStepConfig.alertDescription}
        isNextDisabled={isNextDisabled}
        hideSkip={shouldHideSkip}
        lastStepButtonLabel={areAllStepsCompleted ? 'Go to Dashboard' : 'Skip to Dashboard'}
        hasSkippedBefore={hasSkippedBefore}
      />
    </div>
  );
}
